import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faPhone, 
  faHome, 
  faUtensils,
  faMapMarkerAlt,
  faClock,
  faTruck,
  faStore
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const OrderSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get order data from navigation state
  const orderData = location.state || {};
  const { orderId, total, customerName, deliveryType } = orderData;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Redirect to menu if no order data
    if (!orderId) {
      setTimeout(() => {
        navigate('/menu');
      }, 2000);
    }
  }, [orderId, navigate]);

  // If no order data, show loading/redirect message
  if (!orderId) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pt-32 pb-20 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#9CC241] mx-auto mb-4"></div>
          <p className="text-white text-lg">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-40 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <FontAwesomeIcon 
            icon={faCheckCircle} 
            className="text-[#9CC241] text-7xl mb-6 animate-bounce"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            ¡Pedido <span className="text-[#9CC241]">Enviado</span>!
          </h1>
          <p className="text-gray-300 text-lg md:text-xl">
            Gracias por tu orden, <span className="text-white font-semibold">{customerName}</span>
          </p>
        </div>

        {/* Order Info Card */}
        <div className="bg-white bg-opacity-5 backdrop-blur-md border border-white border-opacity-10 rounded-2xl p-8 mb-8">
          <div className="text-center mb-6">
            <p className="text-gray-400 text-sm mb-2">Número de orden</p>
            <p className="text-[#9CC241] text-3xl font-bold tracking-wider">
              {orderId}
            </p>
          </div>

          <div className="border-t border-white border-opacity-10 pt-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-1">Total</p>
                <p className="text-white text-2xl font-bold">${total?.toLocaleString()}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-1">Tipo</p>
                <div className="flex items-center justify-center gap-2 text-white text-lg font-semibold">
                  <FontAwesomeIcon 
                    icon={deliveryType === 'delivery' ? faTruck : faStore} 
                    className="text-white"
                  />
                  <span>{deliveryType === 'delivery' ? 'Entrega a domicilio' : 'Recoger en tienda'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What's Next Section */}
        <div className="bg-gradient-to-br from-[#9CC241] to-[#7da030] rounded-2xl p-8 mb-8 text-black">
          <h2 className="text-2xl font-bold mb-4">¿Qué sigue?</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-black bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <p className="font-semibold">Confirmación de orden</p>
                <p className="text-sm text-black text-opacity-80">
                  Nuestro equipo revisará tu pedido inmediatamente
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="bg-black bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <p className="font-semibold">Te contactaremos</p>
                <p className="text-sm text-black text-opacity-80">
                  Recibirás un mensaje via Whatsapp para confirmar tu pedido y coordinar {deliveryType === 'delivery' ? 'la entrega' : 'la recogida'}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="bg-black bg-opacity-20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <p className="font-semibold">Pago y entrega</p>
                <p className="text-sm text-black text-opacity-80">
                  El pago se coordinará via whatsapp si sera por trasferencia o en efectivo a la entrega o recogida del pedido
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white bg-opacity-5 backdrop-blur-md border border-white border-opacity-10 rounded-2xl p-6 mb-8">
          <h3 className="text-white text-xl font-bold mb-4 text-center">
            Información de contacto
          </h3>
          <div className="space-y-3">
            <a 
              href="https://wa.me/5363518498" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-gray-300 hover:text-[#9CC241] transition-colors group"
            >
              <div className="bg-[#9CC241] bg-opacity-20 rounded-full w-10 h-10 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                <FontAwesomeIcon icon={faWhatsapp} className="text-[#9CC241]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">WhatsApp</p>
                <p className="font-semibold">+53 6351 8498</p>
              </div>
            </a>
            
            <a 
              href="tel:+5363518498"
              className="flex items-center gap-3 text-gray-300 hover:text-[#9CC241] transition-colors group"
            >
              <div className="bg-[#9CC241] bg-opacity-20 rounded-full w-10 h-10 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                <FontAwesomeIcon icon={faPhone} className="text-[#9CC241]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Teléfono</p>
                <p className="font-semibold">+53 6351 8498</p>
              </div>
            </a>
            
            <div className="flex items-center gap-3 text-gray-300">
              <div className="bg-[#9CC241] bg-opacity-20 rounded-full w-10 h-10 flex items-center justify-center">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-[#9CC241]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Ubicación</p>
                <p className="font-semibold">Obispo 255, La Habana, Cuba</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 text-gray-300">
              <div className="bg-[#9CC241] bg-opacity-20 rounded-full w-10 h-10 flex items-center justify-center">
                <FontAwesomeIcon icon={faClock} className="text-[#9CC241]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Horario</p>
                <p className="font-semibold">Todos los días: 12:00 PM - 12:00 AM</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => {
              navigate('/menu');
              setTimeout(() => {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
              }, 100);
            }}
            className="flex items-center justify-center gap-2 bg-[#9CC241] text-black px-8 py-4 rounded-full font-semibold hover:bg-[#8ab534] transition-all duration-300 shadow-lg hover:shadow-[#9CC241]/50"
          >
            <FontAwesomeIcon icon={faUtensils} />
            <span>Ver Menú</span>
          </button>
          
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
              }, 100);
            }}
            className="flex items-center justify-center gap-2 bg-white bg-opacity-10 text-white px-8 py-4 rounded-full font-semibold hover:bg-opacity-20 transition-all duration-300 border border-white border-opacity-20"
          >
            <FontAwesomeIcon icon={faHome} />
            <span>Ir al Inicio</span>
          </button>
        </div>

        {/* Thank You Message */}
        <div className="text-center mt-12">
          <p className="text-gray-400 text-lg italic">
            "Cada momento en ADASTRA es único. No dejes que se escape tu próxima experiencia inolvidable."
          </p>
          <p className="text-[#9CC241] font-semibold mt-4">
            ¡Gracias por elegir ADASTRA!
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;